import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Tv, Smartphone, Radio, Cloud, Shield, BarChart3, CircleDollarSign,
  Play, Sparkles, Check, Server, Layers, HardDrive, Cast, Cog,
} from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { SERVICES } from "@/lib/i18n";

const Icons = {
  "core-cms": Server,
  "front-apps": Layers,
  "live-streaming": Radio,
  "cloud-playout": Cloud,
  "transcoder": Sparkles,
  "dvr-catchup": Play,
  "secure": Shield,
  "monetization": CircleDollarSign,
  "stb": HardDrive,
  "casting": Cast,
  "analytics": BarChart3,
  "ingest": Cog,
};

export default function Services() {
  const { lang, t } = useI18n();
  const services = pickLang(SERVICES, lang);

  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="overline">{t("nav.services")}</div>
          <h1 className="font-display mt-6 text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] max-w-4xl text-white">
            {lang === "fr" ? <>La <span className="gradient-text">stack streaming</span> complète — modulaire et prête.</>
            : lang === "es" ? <>La <span className="gradient-text">stack de streaming</span> completa — modular y lista.</>
            : lang === "pt" ? <>A <span className="gradient-text">stack de streaming</span> completa — modular e pronta.</>
            : <>The full <span className="gradient-text">streaming stack</span> — modular, opinionated, ready.</>}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
            {lang === "fr" ? "Choisissez ce dont vous avez besoin. Branchez ce que nous construisons. Des applis que vos viewers ouvrent au CDN qui livre chaque image — chaque couche est conçue pour fonctionner ensemble."
            : lang === "es" ? "Elige lo que necesitas. Conecta lo que construimos. Desde las apps que tocan tus espectadores hasta el edge CDN — cada capa está diseñada para trabajar junta."
            : lang === "pt" ? "Escolha o que precisa. Ligue o que construímos. Das apps que os seus espectadores tocam ao edge CDN — cada camada foi feita para trabalhar em conjunto."
            : "Pick what you need. Plug in what we build. From the apps your viewers tap to the CDN edge that delivers every frame — every layer is engineered to work together."}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
          {services.map((s, i) => {
            const Icon = Icons[s.slug] || Sparkles;
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }}
                className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
                data-testid={`service-section-${s.slug}`}
              >
                <div className={`md:col-span-6 ${reverse ? "md:[direction:ltr]" : ""}`}>
                  <div className="w-14 h-14 rounded-2xl gradient-bg flex items-center justify-center text-black">
                    <Icon size={26} strokeWidth={2.2} />
                  </div>
                  <h2 className="font-display mt-6 text-3xl md:text-5xl font-bold tracking-tighter leading-[1.05] text-white">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-lg text-slate-300 leading-relaxed">{s.summary}</p>
                  <ul className="mt-8 space-y-3">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 items-start text-slate-200">
                        <span className="mt-1 w-5 h-5 rounded-full gradient-bg flex items-center justify-center shrink-0">
                          <Check size={12} strokeWidth={3} className="text-black" />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-more-${s.slug}`}
                    className="mt-8 inline-flex items-center gap-2 font-semibold text-[#1FA8FF] hover:text-[#1FE0A8] transition-colors text-sm"
                  >
                    {lang === "fr" ? "En savoir plus" : lang === "es" ? "Saber más" : lang === "pt" ? "Saber mais" : "Learn more"} <ArrowRight size={14} />
                  </Link>
                </div>
                <div className={`md:col-span-6 ${reverse ? "md:[direction:ltr]" : ""}`}>
                  <div className="relative">
                    <div className="absolute -inset-6 rounded-3xl gradient-bg opacity-15 blur-3xl" />
                    <div className="relative glass rounded-3xl aspect-[4/3] overflow-hidden p-8 flex flex-col justify-between">
                      <div className="bg-grid absolute inset-0 opacity-40" />
                      <div className="relative">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{`${(i + 1).toString().padStart(2, "0")}`}</div>
                        <div className="mt-3 font-display text-2xl font-bold text-slate-100">{s.title}</div>
                      </div>
                      <div className="relative grid grid-cols-2 gap-3">
                        {s.bullets.slice(0, 4).map((b, k) => (
                          <div key={k} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-xs text-slate-300">
                            {b}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline justify-center inline-flex">
            {lang === "fr" ? "Vous ne savez pas par où commencer ?"
            : lang === "es" ? "¿No sabes por dónde empezar?"
            : lang === "pt" ? "Não sabe por onde começar?"
            : "Not sure where to start?"}
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {lang === "fr" ? <>Nous mappons votre <span className="gradient-text">déploiement complet</span> en 30 minutes.</>
            : lang === "es" ? <>Mapeamos tu <span className="gradient-text">despliegue completo</span> en 30 minutos.</>
            : lang === "pt" ? <>Mapeamos o seu <span className="gradient-text">rollout completo</span> em 30 minutos.</>
            : <>We'll map your <span className="gradient-text">whole rollout</span> in 30 minutes.</>}
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="services-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/pricing" data-testid="services-cta-pricing" className="btn-ghost">{t("nav.pricing")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
